/**
 * Factory for the dropdown menu
 */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';

import { ExpandableMenuItem, MenuItem, MenuItemHeader } from 'components';
import { BPOINT, breakPoints } from 'constants/breakpoints';
import actions from 'core/script/state/actions/multi-currency';

import Categories from './dropdown-menu-categories.json';
import DiscountCodes from './dropdown-menu-discountcodes.json';
import { decorateUrlWithItm } from 'utils/itm';

import CartIcon from '@tradera/tradera-ui/icons/cart.svg?jsx';
import BoxIcon from '@tradera/tradera-ui/icons/box.svg?jsx';
import HelpIcon from '@tradera/tradera-ui/icons/ellipsis-chat.svg?jsx';
import LastChanceIcon from '@tradera/tradera-ui/icons/last-chance.svg?jsx';
import PriceIcon from '@tradera/tradera-ui/icons/price.svg?jsx';
import AddIcon from '@tradera/tradera-ui/icons/add.svg?jsx';
import MailIcon from '@tradera/tradera-ui/icons/mail.svg?jsx';
import BookmarkIcon from '@tradera/tradera-ui/icons/bookmark.svg?jsx';
import ExitIcon from '@tradera/tradera-ui/icons/exit.svg?jsx';
import UserIcon from '@tradera/tradera-ui/icons/user.svg?jsx';
import EditIcon from '@tradera/tradera-ui/icons/edit.svg?jsx';
import BulbIcon from '@tradera/tradera-ui/icons/bulb.svg?jsx';
import HeartIcon from '@tradera/tradera-ui/icons/heart.svg?jsx';
import TagIcon from '@tradera/tradera-ui/icons/tag.svg?jsx';
import FireIcon from '@tradera/tradera-ui/icons/fire.svg?jsx';
import BiddingIcon from '@tradera/tradera-ui/icons/bidding.svg?jsx';
import ForYouIcon from '@tradera/tradera-ui/icons/for-you.svg?jsx';
import TenIcon from '@tradera/tradera-ui/icons/10kr.svg?jsx';

import 'components/trending/trending-tags.scss';

const STATIC_IMAGES_BASE_URL =
    'https://static.tradera.net/touchweb/static/images';

const MenuItemSeparator = ({ label, cssClass }) => (
    <li className={`site-dropdown__separator text-center ${cssClass || ''}`}>
        {label}
    </li>
);

MenuItemSeparator.propTypes = {
    label: PropTypes.node
};

const Brand = ({ label, link, itm }) => (
    <a
        type="button"
        className="btn btn-outline-primary btn-sm mr-3 my-2 trending-tag flex-shrink-0"
        href={itm ? decorateUrlWithItm(link, itm) : link}>
        <TagIcon className="icon icon-sm icon-primary mr-2" />
        {label}
    </a>
);

const DiscountCategoryItem = ({ label, link, itm }) => (
    <a
        type="button"
        className="btn btn-outline-primary btn-sm mr-3 my-2 trending-tag flex-shrink-0"
        href={itm ? decorateUrlWithItm(link, itm) : link}>
        {label}
    </a>
);

const Search = ({ label, link, itm }) => (
    <a
        type="button"
        className="btn btn-outline-primary btn-sm mr-3 my-2 trending-tag flex-shrink-0"
        href={itm ? decorateUrlWithItm(link, itm) : link}>
        <FireIcon className="icon icon-sm mr-2" />
        {label}
    </a>
);

const BrandsMenuItem = ({ children, cssClassLabel, cssClassTags, itm }) => (
    <li>
        <div className={`${cssClassLabel || ''} pt-3 pb-2`}>
            <strong className="pl-3">Populära varumärken:</strong>
        </div>
        <div
            className={`${cssClassTags ||
                ''} tag-wrapper d-flex flex-nowrap flex-md-wrap align-items-center py-2 py-md-0 mr-3`}>
            {children.map((brand, index) => (
                <Brand
                    key={index}
                    label={brand.label}
                    link={brand.link}
                    itm={itm}
                />
            ))}
        </div>
    </li>
);

const DiscountCategoryMenuItem = ({ discountCategory, itm }) => (
    <li>
        <div className="indent-1 pt-3 pb-2">
            <strong className="pl-3">{discountCategory.label}</strong>
        </div>
        <div className="indent-1 tag-wrapper d-flex flex-nowrap flex-md-wrap align-items-center py-2 py-md-0 mr-3">
            {discountCategory.items.map((item, index) => (
                <DiscountCategoryItem
                    key={index}
                    label={item.label}
                    link={item.link}
                    itm={itm}
                />
            ))}
        </div>
    </li>
);

const SearchesMenuItem = ({ children, cssClassLabel, cssClassTags, itm }) => (
    <li>
        <div className={`${cssClassLabel || ''} pt-3 pb-2`}>
            <strong className="pl-3">Trendande sökningar:</strong>
        </div>
        <div
            className={`${cssClassTags ||
                ''} tag-wrapper tags-searches d-flex flex-nowrap flex-md-wrap align-items-center py-2 py-md-0 mr-3`}>
            {children.map((brand, index) => (
                <Search
                    key={index}
                    label={brand.label}
                    link={brand.link}
                    itm={itm}
                />
            ))}
        </div>
    </li>
);

const createMenuCategoryItem = ({ label, link, bold }, index, level, itm) => (
    <MenuItem
        key={index}
        label={label}
        link={link}
        itm={itm}
        bold={bold || false}
        cssClass={`site-dropdown__item-default indent-${level}`}
    />
);

const createMenuCategoryItems = (item, index, level, itm) => {
    return !item.items ? (
        createMenuCategoryItem(item, index, level, itm)
    ) : (
        <ExpandableMenuItem
            key={index}
            label={item.label}
            cssClass={`site-dropdown__item-with-image indent-${level}`}
            icon={
                item.image && (
                    <img
                        className="category-header-image"
                        srcSet={`${STATIC_IMAGES_BASE_URL}/${
                            item.image.srcSet.regular
                        } 1x, ${STATIC_IMAGES_BASE_URL}/${
                            item.image.srcSet.retina
                        } 2x`}
                        src={`${STATIC_IMAGES_BASE_URL}/${
                            item.image.srcSet.regular
                        }`}
                    />
                )
            }
            border={level > 0 ? false : true}>
            {item.brands && (
                <BrandsMenuItem
                    cssClassLabel={`indent-${level + 1}`}
                    cssClassTags={`indent-1 indent-md-${level + 1}`}
                    itm={itm}>
                    {item.brands}
                </BrandsMenuItem>
            )}
            {item.searches && (
                <SearchesMenuItem
                    cssClassLabel={`indent-${level + 1}`}
                    cssClassTags={`indent-1 indent-md-${level + 1}`}
                    itm={itm}>
                    {item.searches}
                </SearchesMenuItem>
            )}
            {item.items.map((item, index) =>
                createMenuCategoryItems(item, index, level + 1, itm)
            )}
        </ExpandableMenuItem>
    );
};

const sellingLink = (isLoggedIn, showHowToSell) => {
    if (!isLoggedIn && showHowToSell) {
        return '/info/how-to-sell';
    } else {
        return '/selling';
    }
};

const shouldShowCurrency = (showCurrency, preferredCurrency, currencies) => {
    return (
        showCurrency && preferredCurrency && currencies && currencies.length > 0
    );
};

const DropdownMenuContent = ({
    initData: {
        isLoggedIn,
        pageName,
        featureSwitches: {
            messaging: showMessaging,
            'multi-currency': showCurrency,
            'how-to-sell-page': showHowToSell,
            'discount-codes-in-hamburger': showDiscountCodes
        }
    },
    formattedCartItems,
    threadsUnread,
    preferredCurrency,
    currencies,
    dispatch
}) => {
    const itm = {
        source: pageName || 'undefined',
        medium: 'dropdown-menu'
    };

    const mediaProps = { maxWidth: breakPoints[BPOINT.medium].max };

    const displayCurrency = shouldShowCurrency(
        showCurrency,
        preferredCurrency,
        currencies
    );

    return (
        <Fragment>
            {/* NOTE: Notifications are disabled for burger button for desktop due to hidden cart and messaging, see DropDownMenuHamburger */}
            <MediaQuery {...mediaProps}>
                {formattedCartItems > 0 && (
                    <MenuItem
                        label="Varukorg"
                        link="/shopping/cartoverview"
                        cssClass="site-dropdown__item-blue-with-icon"
                        showArrow
                        icon={<CartIcon className="icon" />}
                        notify={formattedCartItems}
                        border
                        follow={false}
                    />
                )}
                {!isLoggedIn && (
                    <Fragment>
                        <MenuItem
                            label="Logga in"
                            link="/my/overview"
                            cssClass="site-dropdown__item-with-icon-small pt-2"
                            icon={<UserIcon className="icon" />}
                        />
                        <MenuItem
                            label="Bli medlem"
                            link="/register"
                            cssClass="site-dropdown__item-with-icon-small"
                            icon={<EditIcon className="icon" />}
                        />
                    </Fragment>
                )}
                <MenuItem
                    label={isLoggedIn ? 'Skapa annons' : 'Så säljer du'}
                    link={sellingLink(isLoggedIn, showHowToSell)}
                    cssClass={`site-dropdown__item-with-icon-small ${
                        isLoggedIn ? 'pt-2' : ''
                    }`}
                    icon={<AddIcon className="icon" />}
                    follow={false}
                />
                {isLoggedIn &&
                    showMessaging && (
                        <MenuItem
                            label="Meddelanden"
                            link="/messaging"
                            cssClass="site-dropdown__item-with-icon-small"
                            icon={<MailIcon className="icon" />}
                            notify={threadsUnread}
                            follow={false}
                        />
                    )}
                <MenuItem
                    label="Mina budgivningar"
                    link="/my/active-items"
                    cssClass="site-dropdown__item-with-icon-small"
                    icon={<BiddingIcon className="icon" />}
                    follow={false}
                />
                <MenuItem
                    label="Minneslista"
                    link="/my/wish-list"
                    cssClass="site-dropdown__item-with-icon-small"
                    icon={<HeartIcon className="icon" />}
                    follow={false}
                />
                <MenuItem
                    label="Bevakningar"
                    link="/my/saved-searches"
                    cssClass={`site-dropdown__item-with-icon-small ${
                        isLoggedIn ? '' : 'pb-2'
                    }`}
                    icon={<BookmarkIcon className="icon" />}
                    border={isLoggedIn ? false : true}
                    follow={false}
                />
                {isLoggedIn && (
                    <MenuItem
                        label="Logga ut"
                        link="/login/logout"
                        cssClass="site-dropdown__item-with-icon-small pb-2"
                        icon={<ExitIcon className="icon" />}
                        border
                        follow={false}
                    />
                )}
            </MediaQuery>
            <MenuItem
                label={isLoggedIn ? 'Bara för dig' : 'Trendar på Tradera'}
                link="/ma/endless-recommendation"
                cssClass="site-dropdown__item-blue-with-icon"
                showArrow
                icon={<ForYouIcon className="icon" />}
                border
                itm={itm}
                follow={false}
            />
            <MenuItem
                label="Sista chansen"
                link="/campaign/sista-chansen"
                cssClass="site-dropdown__item-blue-with-icon"
                showArrow
                icon={<LastChanceIcon className="icon" />}
                border
                itm={itm}
            />
            <MenuItem
                label="Under 10 kr"
                link={`/campaign/under10?sortBy=MostBids`}
                cssClass="site-dropdown__item-blue-with-icon"
                showArrow
                icon={<TenIcon className="icon" />}
                border
                itm={itm}
            />
            <MenuItem
                label="Visa alla kategorier"
                link="/kategorier"
                cssClass="site-dropdown__item-blue-with-icon"
                showArrow
                icon={<BoxIcon className="icon" />}
                border
                itm={itm}
            />
            <MenuItemSeparator label={<strong>Utvalda kategorier:</strong>} />
            {Categories.categories.map((item, index) =>
                createMenuCategoryItems(item, index, 0, itm)
            )}
            <MenuItem
                label="Visa alla kategorier"
                link="/kategorier"
                cssClass="site-dropdown__item-with-icon"
                showArrow
                icon={<BoxIcon className="icon" />}
                border
                itm={itm}
            />
            <MenuItem
                label="Aktuella kampanjer"
                link="/rekommenderat"
                cssClass="site-dropdown__item-with-icon"
                showArrow
                icon={<BulbIcon className="icon" />}
                border
                itm={itm}
            />
            <MenuItem
                label="Kundservice"
                link="https://info.tradera.com/"
                cssClass="site-dropdown__item-with-icon"
                showArrow
                icon={<HelpIcon className="icon" />}
                border
                itm={itm}
            />
            {displayCurrency && (
                <ExpandableMenuItem
                    label={
                        <Fragment>
                            <strong className="mr-3">
                                {preferredCurrency.shortName}
                            </strong>
                        </Fragment>
                    }
                    cssClass="site-dropdown__item-with-icon"
                    icon={<PriceIcon className="icon" />}
                    border>
                    {currencies.map((currency, index) => (
                        <MenuItem
                            key={index}
                            label={
                                <Fragment>
                                    <strong className="mr-3">
                                        {currency.shortName}
                                    </strong>
                                    {currency.longName}
                                </Fragment>
                            }
                            cssClass="site-dropdown__item-default indent-1"
                            onClick={() => {
                                dispatch(
                                    actions.setPreferredCurrency(currency.code)
                                );
                            }}
                        />
                    ))}
                </ExpandableMenuItem>
            )}
            {!showDiscountCodes ? (
                <MenuItem
                    label="Rabattkoder"
                    link="/rabattkoder"
                    cssClass="site-dropdown__item-with-icon"
                    showArrow
                    icon={<PriceIcon className="icon" />}
                    border
                    itm={itm}
                />
            ) : (
                <ExpandableMenuItem
                    label={DiscountCodes.content.label}
                    cssClass="site-dropdown__item-with-icon"
                    icon={<PriceIcon className="icon" />}
                    border>
                    {DiscountCodes.content.categories.map((category, index) => (
                        <DiscountCategoryMenuItem
                            discountCategory={category}
                            key={index}
                            itm={itm}
                        />
                    ))}
                    <MenuItemHeader
                        label={DiscountCodes.content.codes.label}
                        bold={DiscountCodes.content.codes.bold}
                    />
                    {DiscountCodes.content.codes.items.map((item, index) => (
                        <MenuItem
                            key={index}
                            label={item.label}
                            itm={itm}
                            cssClass="site-dropdown__item-default indent-1"
                            link={item.link}
                            bold={item.bold || false}
                        />
                    ))}
                </ExpandableMenuItem>
            )}
            {/* NOTE: Fix for iPhone menu making bottom element unclickable */}
            <MediaQuery {...mediaProps}>
                <MenuItemSeparator cssClass="site-dropdown__separator--bottom" />
            </MediaQuery>
        </Fragment>
    );
};

export default DropdownMenuContent;



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/components/site-header/dropdown-menu-content.jsx